import React, { useState } from "react";
import {
  authenticateUser,
  completeScreenAction,
  downloadFile,
  getToken,
} from "../../functions/api";
import { saveResponse } from "../../functions/utils";
import { useLoader, type Loader } from "../../hooks/Loader";
import type { ApplicationContextProviderProps } from "./types";
import type { TokenSessionDetails, Screen, AppContextType } from "./types";
import { evaluateCondition } from "./utils";
import { useModal } from "../common/ModalContext";

export const AppContext = React.createContext<AppContextType | null>(null);

export function ApplicationContextProvider(props: ApplicationContextProviderProps) {
  const [currentScreen, setCurrentScreen] = useState<Screen | undefined>();
  const [details, setDetails] = useState<TokenSessionDetails | undefined>();
  const [token, setToken] = useState<string | undefined>();
  const [applicationError, setApplicationError] = useState(false);
  const loader = useLoader(false) as Loader;
  const modal = useModal();

  const loadToken = (token: string) => {
    if (loader.loading || loader.error) return;
    setToken(token);
    loader.setLoading();
    getToken(token)
      .then((res) => {
        loader.setSuccess();
        setDetails(res.data);
        const screen = res.data.campaignDetails?.screens.find(
          (s) => s.name === res.data.currentScreen
        );
        setCurrentScreen(screen);
      })
      .catch(() => {
        loader.setError();
      });
  };

  const authenticate = async (token: string, validationKey: string) => {
    await authenticateUser(token, validationKey);
    setDetails((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        needsAuthentication: false,
      };
    });
  };

  const resolveScreenAction = async (data: Record<string, any>) => {

    if (!currentScreen || !details) return;

    for (let i = 0; i < currentScreen.actions.length; i++) {
      const action = currentScreen.actions[i];
      if (action.condition) {
        const conditionMet = evaluateCondition(action.condition, (fieldName) => data[fieldName]);
        if (!conditionMet) {
          continue;
        }
      }
      switch (action.actionType) {
        case "confirmation":
          if (action.confirmationDetails) {
            const result = await modal.showModal({
              title: action.confirmationDetails.title,
              message: action.confirmationDetails.message,
              yesButtonText: action.confirmationDetails.yesButtonText,
              noButtonText: action.confirmationDetails.noButtonText,
              variant: "warning",
            });
            if (result === "no") {
              return; // User clicked no, stop processing actions
            }
          }
          break;
        case "callback":
        case "setResponse":
          if (token) {
            await completeScreenAction(token, data, currentScreen.name);
          }
          break;
        case "download":
        case "openDocument":
          if (token) {
            var result = await downloadFile(token, currentScreen.name);
            if (result.status >= 400) {
              setApplicationError(true);
              return;
            }

            saveResponse(result, action.actionType);
          }
          break;
        case "moveToScreen":
          var nextScreen = details.campaignDetails?.screens.find(
            (s) => s.name === action.moveToScreen
          );
          if (nextScreen) {
            setCurrentScreen(nextScreen);
          }
          break;
        case "completeCampaign":
          setDetails({
            ...details,
            completed: true,
          });
          break;
      }
    }
  };

  const contextValue: AppContextType = {
    token,
    loader,
    details,
    isTokenLoaded: !!details,
    bgHook: props.bgHook,
    applicationError,
    currentScreen,
    resolveScreenAction,
    loadToken,
    authenticate,
    setApplicationError: () => setApplicationError(true),
  };

  return (
    <AppContext.Provider value={contextValue}>
      {props.children}
    </AppContext.Provider>
  );
}
